import { create } from "zustand";
import { getUnits, type CourseId } from "../../learn/engine/lessonRegistry";

export type LessonStatus = "notStarted" | "started" | "finished";

export interface LessonLocation {
  course: CourseId;
  unit: number;
  lesson: number;
}

export interface UnitProgressInfo {
  statuses: LessonStatus[];
}

export interface CourseProgress {
  units: UnitProgressInfo[];
  currentUnit: number;
  currentLesson: number;
  hasStarted: boolean;
}

interface ProgressState {
  initialized: boolean;
  activeCourse: CourseId;
  courses: Record<CourseId, CourseProgress>;
  isHomescreen: boolean;

  init(): void;
  setActiveCourse(course: CourseId): void;
  setLocation(course: CourseId, unit: number, lesson: number): void;
  finishLesson(): LessonLocation;
  lessonStarted(course: CourseId, unit: number, lesson: number): void;
  getLessonStatus(course: CourseId, unit: number, lesson: number): LessonStatus;
  getNumLessons(course: CourseId, unit: number): number;
  getNumUnits(course: CourseId): number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function emptyCourse(): CourseProgress {
  return { units: [], currentUnit: 1, currentLesson: 1, hasStarted: false };
}

function seedCourse(course: CourseId): CourseProgress {
  const units: UnitProgressInfo[] = getUnits(course).map((unit) => ({
    statuses: unit.lessons.map(() => "notStarted" as LessonStatus),
  }));
  return { units, currentUnit: 1, currentLesson: 1, hasStarted: false };
}

export const useProgressStore = create<ProgressState>()((set, get) => ({
  initialized: false,
  activeCourse: "classic",
  courses: { classic: emptyCourse(), apcsp: emptyCourse() },
  isHomescreen: true,

  init(): void {
    if (get().initialized) return;
    set({
      courses: { classic: seedCourse("classic"), apcsp: seedCourse("apcsp") },
      initialized: true,
    });
  },

  setActiveCourse(course: CourseId): void {
    set({ activeCourse: course });
  },

  setLocation(course: CourseId, unit: number, lesson: number): void {
    const state = get();
    if (!state.initialized) state.init();
    const numUnits = state.getNumUnits(course);
    const safeUnit = clamp(unit, 1, Math.max(numUnits, 1));
    const numLessons = state.getNumLessons(course, safeUnit);
    const safeLesson = clamp(lesson, 1, Math.max(numLessons, 1));
    const previous = get().courses[course];
    set({
      activeCourse: course,
      isHomescreen: false,
      courses: {
        ...get().courses,
        [course]: {
          ...previous,
          currentUnit: safeUnit,
          currentLesson: safeLesson,
          hasStarted: true,
        },
      },
    });
    get().lessonStarted(course, safeUnit, safeLesson);
  },

  finishLesson(): LessonLocation {
    const state = get();
    const course = state.activeCourse;
    const progress = state.courses[course];
    const { currentUnit, currentLesson } = progress;
    const units = progress.units.map((u) => ({ statuses: [...u.statuses] }));
    const unit = units[currentUnit - 1];
    if (unit) unit.statuses[currentLesson - 1] = "finished";

    const numLessons = state.getNumLessons(course, currentUnit);
    const numUnits = state.getNumUnits(course);
    let nextUnit = currentUnit;
    let nextLesson = currentLesson + 1;

    if (nextLesson > numLessons) {
      if (currentUnit < numUnits) {
        nextUnit = currentUnit + 1;
        nextLesson = 1;
      } else {
        nextLesson = numLessons;
      }
    }

    set({
      courses: {
        ...state.courses,
        [course]: {
          units,
          currentUnit: nextUnit,
          currentLesson: nextLesson,
          hasStarted: true,
        },
      },
    });
    get().lessonStarted(course, nextUnit, nextLesson);

    return { course, unit: nextUnit, lesson: nextLesson };
  },

  lessonStarted(course: CourseId, unit: number, lesson: number): void {
    const state = get();
    const progress = state.courses[course];
    const target = progress.units[unit - 1];
    if (!target) return;
    if (target.statuses[lesson - 1] === "notStarted") {
      target.statuses[lesson - 1] = "started";
      set({
        courses: {
          ...state.courses,
          [course]: {
            ...progress,
            units: progress.units.map((u) => ({ statuses: [...u.statuses] })),
          },
        },
      });
    }
  },

  getLessonStatus(
    course: CourseId,
    unit: number,
    lesson: number,
  ): LessonStatus {
    const target = get().courses[course].units[unit - 1];
    if (!target) return "notStarted";
    return target.statuses[lesson - 1] ?? "notStarted";
  },

  getNumLessons(course: CourseId, unit: number): number {
    const target = get().courses[course].units[unit - 1];
    return target ? target.statuses.length : 0;
  },

  getNumUnits(course: CourseId): number {
    return get().courses[course].units.length;
  },
}));
